﻿import { useEffect, useRef, useState } from 'react';
import cm from 'classnames';

import { useResizeObserver } from 'hooks/use-resize-observer';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { Path, useLocation } from 'components/_hooks/use-router';
import { setIntro, setTutorials } from 'components/app/_mediator/app.slice';
import { IntroKeys } from 'components/app/intro/intro-keys';
import styles from './_tutorials.module.scss';

const MARGIN = 4; //px

interface Key {
  el: Element;
  key: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export const Tutorials = () => {
  const { path } = useLocation();
  const intro = useAppSelector((state) => state.app.intro);
  const tutorials = useAppSelector((state) => state.app.tutorials);
  const dispatch = useAppDispatch();
  const ref = useRef(null);

  const [keys, _setKeys] = useState<Key[]>([]);

  const setKeys = () => {
    _setKeys(
      !tutorials
        ? []
        : getKeys(path)
            .map((key) => {
              const el = document.querySelector(`[data-intro="${key}"]`);
              if (!el) return null;
              const rect = el.getBoundingClientRect();
              return {
                el,
                key,
                x: rect.x - MARGIN,
                y: rect.y - MARGIN,
                width: rect.width + MARGIN * 2,
                height: rect.height + MARGIN * 2,
              };
            })
            .filter((key): key is NonNullable<typeof key> => key !== null)
    );
  };

  useResizeObserver(ref.current, setKeys);

  useEffect(() => {
    setKeys();
  }, [tutorials]);

  useEffect(() => {
    if (!intro) setKeys();
  }, [intro]);

  useEffect(() => {
    if (!tutorials || intro) return;
    const handleClick = (e: MouseEvent) => {
      e.stopImmediatePropagation();
      const key = keys.find((key) => key.el.contains(e.target as HTMLElement));
      if (key) dispatch(setIntro(key.key));
      else dispatch(setTutorials(false));
    };
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [tutorials, intro, keys]);

  return (
    <div ref={ref} className={cm(styles.Tutorials, { [styles['Tutorials--active']]: keys.length && !intro })}>
      <svg className={styles.Tutorials__mask}>
        <defs>
          <mask id="multi-hole-mask">
            <rect width="100%" height="100%" fill="white" />
            {keys.map((el, i) => (
              <rect key={i} x={el.x} y={el.y} width={el.width} height={el.height} />
            ))}
          </mask>
        </defs>
        <rect mask="url(#multi-hole-mask)" width="100%" height="100%" />
      </svg>
    </div>
  );
};

/* H E L P E R S */

const getKeys = (path: Path) => {
  switch (path) {
    case Path.projects:
      return [IntroKeys.createProject, IntroKeys.addFiles, IntroKeys.createStory];
    case Path.selector:
      return [IntroKeys.srAddClip];
    case Path.storyboard:
      return [
        IntroKeys.srAddClip,
        IntroKeys.sbClip,
        IntroKeys.storyBoardHeader,
        IntroKeys.sbAudioHeader,
        IntroKeys.sbSettings,
      ];
    case Path.finalcuts:
      return [IntroKeys.fcElem, IntroKeys.fcPlay, IntroKeys.fcUpgrade, IntroKeys.fcDownload, IntroKeys.fcShare];
    default:
      return [];
  }
};
